import { useMemo, useState } from 'react';
import * as R from 'ramda';

import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Progress } from '@atom/mui';
import {
  BudgetCategory,
  BudgetItem,
  BudgetStatus,
  BudgetSummary,
  BudgetType,
  BudgetUnit,
} from '@atom/types/budget';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailFilters from './filtersBar/BudgetDetailFilters';
import BudgetDetailBreadcrumbs from './BudgetDetailBreadcrumbs';
import BudgetDetailContext from './BudgetDetailContext';
import BudgetDetailHeader from './BudgetDetailHeader';
import BudgetDetailStatus from './BudgetDetailStatus';
import BudgetDetailUnit from './BudgetDetailUnit';
import { EditField } from './budgetDetailUtils';
import BudgetFixedHeader from './BudgetFixedHeader';
import { useBudgetBulkEditAccess } from './useBudgetBulkEditAccess';
import useBudgetDetail from './useBudgetDetail';
import { useBudgetFilterStorage } from './useBudgetFilterStorage';

import classes from './budgetDetail.module.css';

const styles = {
  progress: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

const BudgetDetail = () => {
  const [, { params }] = useNavigation();
  const { id: budgetId, unitId } = params;
  const preferences = usePreferences();
  const showAuthorizedBudget = R.pathOr(
    false,
    ['budgeting', 'showAuthorizedBudget'],
    preferences,
  );
  const showExpenditures = R.pathOr(
    false,
    ['budgeting', 'showExpenditures'],
    preferences,
  );
  const showApprovalFlow: boolean = R.pathOr(
    false,
    ['budgeting', 'showApprovalFlow'],
    preferences,
  );
  const showBudgetTypes: boolean = R.pathOr(
    false,
    ['budgeting', 'showBudgetTypes'],
    preferences,
  );

  const {
    existingTypeFilters,
    existingCategoryFilters,
    existingBudgetItemFilters,
    existingComparisonBudgets,
    existingShowTracking,
    existingShowChart,
  } = useBudgetFilterStorage(budgetId);

  const [typeFilters, setTypeFilters] =
    useState<BudgetType[]>(existingTypeFilters);

  const budgetTypes = useMemo(
    () => typeFilters.map(({ name }) => name),
    [typeFilters],
  );

  const {
    budget,
    parentBudgetUnit,
    setParentBudgetUnit,
    loadingParentUnit,
    fetchParentUnit,
    budgetSummaries,
    setBudgetSummaries,
    fetchSummaries,
    loadingSummaries,
    budgetOptions,
    totalBudgetOptions,
    setTotalBudgetOptions,
    loadingBudgetOptions,
    getBudgetOptions,
    showTracking,
    setShowTracking,
    showChart,
    setShowChart,
    comparisonBudgets,
    setComparisonBudgets,
    categoryFilters,
    setCategoryFilters,
    categoryIds,
    budgetItemTemplateFilters,
    setBudgetItemTemplateFilters,
    budgetItemTemplateNames,
  } = useBudgetDetail(budgetId, budgetTypes, {
    unitId,
    showExpenditures,
    existingShowTracking,
    existingShowChart,
    existingComparisonBudgets,
    existingCategoryFilters,
    existingBudgetItemFilters,
  });

  const { hasBulkEditBudgetItemAccess } = useBudgetBulkEditAccess(
    parentBudgetUnit?.actionsOnBudgetItems,
  );
  const selectItemCheckboxDisabled =
    !hasBulkEditBudgetItemAccess ||
    parentBudgetUnit.status !== BudgetStatus.DRAFT;

  const [childBudgetUnits, setChildBudgetUnits] = useState<BudgetUnit[]>(null);
  const [budgetCategories, setBudgetCategories] =
    useState<BudgetCategory[]>(null);

  const [excludeZeroBudgetItems, setExcludeZeroBudgetItems] =
    useState<boolean>(false);

  // Budget Comparisons
  const [openComparisonRowId, setOpenComparisonRowId] = useState<string>();
  const [budgetOverviewSummaries, setBudgetOverviewSummaries] = useState<
    BudgetSummary[]
  >([]);

  // Item Editing
  const [editingItem, setEditingItem] = useState<BudgetItem>();
  const [editingField, setEditingField] = useState<EditField>();
  const [itemHoverId, setItemHoverId] = useState<string>();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set([]),
  );

  // Budget Status Bulk Select
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set([]));

  // Budget Item Bulk Select for Alignment
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set([]));

  return (
    <BudgetDetailContext.Provider
      value={{
        budget,
        parentBudgetUnit,
        setParentBudgetUnit,
        childBudgetUnits,
        setChildBudgetUnits,
        budgetCategories,
        setBudgetCategories,
        categoryFilters,
        categoryIds,
        setCategoryFilters,
        typeFilters,
        setTypeFilters,
        budgetTypes,
        budgetItemTemplateFilters,
        setBudgetItemTemplateFilters,
        budgetItemTemplateNames,
        editingItem,
        setEditingItem,
        editingField,
        setEditingField,
        expandedCategories,
        setExpandedCategories,
        excludeZeroBudgetItems,
        setExcludeZeroBudgetItems,
        loadingParentUnit,
        comparisonBudgets,
        setComparisonBudgets,
        budgetSummaries,
        setBudgetSummaries,
        budgetOverviewSummaries,
        setBudgetOverviewSummaries,
        openComparisonRowId,
        setOpenComparisonRowId,
        fetchSummaries,
        loadingSummaries,
        itemHoverId,
        setItemHoverId,
        showTracking,
        setShowTracking,
        showExpenditures,
        showBudgetTypes,
        showApprovalFlow,
        showChart,
        setShowChart,
        loadingBudgetOptions,
        totalBudgetOptions,
        setTotalBudgetOptions,
        budgetOptions,
        getBudgetOptions,
        selectedUnits,
        setSelectedUnits,
        selectedItems,
        setSelectedItems,
        fetchParentUnit,
        showAuthorizedBudget,
        selectItemCheckboxDisabled,
      }}
    >
      <>
        {isNilOrEmpty(budget) ? (
          <Progress style={styles.progress} />
        ) : (
          <>
            <BudgetDetailHeader />
            <div className={classes.container}>
              <BudgetFixedHeader>
                <div className={classes.breadcrumbRow}>
                  <BudgetDetailBreadcrumbs />
                  {showApprovalFlow && <BudgetDetailStatus />}
                </div>
                <BudgetDetailFilters />
              </BudgetFixedHeader>
              <BudgetDetailUnit />
            </div>
          </>
        )}
      </>
    </BudgetDetailContext.Provider>
  );
};

export default BudgetDetail;
